// src/lib/utils/safeUrl.ts
// Single helper for every owner-controlled URL that ends up as a navigation
// target or a link: Form.redirectUrl, settings.privacyPolicyUrl. See
// docs/unsafe-url-schemes_plan.md and docs/unsafe-url-schemes_findings.md.
//
// The only check that matters is the parsed scheme. `javascript:`, `data:`,
// `vbscript:`, `file:` and `blob:` all parse fine as URLs; they are simply not
// http(s). Letter case, leading whitespace and embedded tabs/newlines inside
// the scheme are normalized away by the URL constructor itself (confirmed in
// Node: `new URL("java\tscript:x").protocol` is `"javascript:"`, same as
// `new URL("JAVASCRIPT:x").protocol`), so there is nothing extra to strip
// before parsing — a regex pre-check would only add a second place to get the
// normalization wrong.

export interface SafeNavigationUrlOptions {
  /** Accept a path starting with a single `/`, for the privacy link. */
  allowRelative?: boolean;
}

/**
 * The value as it can safely navigate or link, trimmed, or null.
 *
 * A protocol-relative value (`//host/path`) carries no scheme for the URL
 * constructor to reject on its own, so it is parsed against an assumed
 * `https:` base; a real web address, however the owner wrote it, is allowed
 * through either way. `allowRelative` additionally accepts a same-origin path,
 * but never one browsers could misread as protocol-relative: a leading `//`
 * or `/\` collapses to the attacker's host once a browser normalizes
 * backslashes to slashes, so both are rejected rather than treated as a path.
 */
export function safeNavigationUrl(
  value: unknown,
  options: SafeNavigationUrlOptions = {},
): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed) return null;

  if (trimmed.startsWith("//")) {
    try {
      return new URL(`https:${trimmed}`).protocol === "https:" ? trimmed : null;
    } catch {
      return null;
    }
  }

  if (options.allowRelative && isSafeRelativePath(trimmed)) {
    return trimmed;
  }

  try {
    const protocol = new URL(trimmed).protocol;
    return protocol === "http:" || protocol === "https:" ? trimmed : null;
  } catch {
    return null;
  }
}

/**
 * A single leading `/` not followed by another `/` or a `\`, and with no
 * ASCII control character or whitespace anywhere in the value. A browser
 * strips tabs, newlines and carriage returns before reading a URL, so
 * `/\t/evil.com` is read as `//evil.com`; the other control characters and
 * plain space are rejected too, since none of them belong unencoded in a
 * path.
 */
function isSafeRelativePath(value: string): boolean {
  if (value[0] !== "/") return false;
  const second = value[1];
  if (second === "/" || second === "\\") return false;
  return !/[\x00-\x20\x7f]/.test(value);
}
